import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, StatCard, Panel, Badge, Table, Button,
} from "../components/UIKit";
import {
  Users, Activity, Workflow, AlertTriangle, Mic, HeartPulse,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
  const { api } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [algos, setAlgos] = useState([]);

  useEffect(() => {
    api("/quality/stats").then(setStats).catch(() => {});
    api("/jobs").then((d) => setJobs(d.items || [])).catch(() => {});
    api("/algorithms").then((d) => setAlgos(d.items || [])).catch(() => {});
  }, [api]);

  return (
    <div className="p-8">
      <PageHeader
        title="Panoramica"
        subtitle="Stato del sistema SmartDream in tempo reale"
      />

      <div className="grid grid-cols-4 gap-3">
        <StatCard
          label="Utenti attivi"
          value={stats?.active_devices_24h ?? "—"}
          sub={`su ${stats?.total_users ?? "—"} totali`}
          icon={<Users size={14} />}
        />
        <StatCard
          label="Sessioni 24h"
          value={stats?.sessions_24h ?? "—"}
          sub={`${stats?.pct_complete ?? 0}% complete`}
          accent="sky"
          icon={<Activity size={14} />}
        />
        <StatCard
          label="HRV attivo"
          value={`${stats?.pct_hrv ?? 0}%`}
          sub="delle sessioni 24h"
          accent="teal"
          icon={<HeartPulse size={14} />}
        />
        <StatCard
          label="Microfono"
          value={`${stats?.pct_mic ?? 0}%`}
          sub="consenso granted"
          accent="amber"
          icon={<Mic size={14} />}
        />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <Panel
          title="Job in esecuzione"
          right={<Link to="/admin/scheduler" className="text-[#67e8d1] text-[12px] font-medium">Apri scheduler →</Link>}
        >
          <Table
            columns={[
              { key: "name", label: "Nome" },
              {
                key: "status",
                label: "Stato",
                render: (r) => (
                  <Badge
                    color={
                      r.last_run_status === "completed"
                        ? "teal"
                        : r.last_run_status === "failed"
                        ? "rose"
                        : r.last_run_status === "running"
                        ? "amber"
                        : "slate"
                    }
                  >
                    {r.last_run_status || r.status}
                  </Badge>
                ),
              },
              {
                key: "last_run_at",
                label: "Ultima esecuzione",
                render: (r) =>
                  r.last_run_at ? new Date(r.last_run_at).toLocaleString("it-IT") : "—",
              },
            ]}
            rows={jobs.slice(0, 5)}
          />
        </Panel>

        <Panel
          title="Algoritmi"
          right={<Link to="/admin/algorithms" className="text-[#67e8d1] text-[12px] font-medium">Gestisci →</Link>}
        >
          <Table
            columns={[
              { key: "name", label: "Nome" },
              { key: "current_version", label: "Versione" },
              {
                key: "status",
                label: "Stato",
                render: (r) => (
                  <Badge
                    color={
                      r.status === "active"
                        ? "teal"
                        : r.status === "testing"
                        ? "amber"
                        : r.status === "deprecated"
                        ? "rose"
                        : "slate"
                    }
                  >
                    {r.status}
                  </Badge>
                ),
              },
            ]}
            rows={algos}
          />
        </Panel>
      </div>

      <div className="mt-4">
        <Panel
          title="Anomalie recenti"
          right={
            <Link to="/admin/quality" className="text-[#67e8d1] text-[12px] font-medium">
              Dettagli qualità →
            </Link>
          }
        >
          {stats?.anomalies?.length ? (
            <div className="flex flex-wrap gap-2">
              {stats.anomalies.slice(0, 10).map((a) => (
                <div
                  key={a.id}
                  className="rounded-lg border border-amber-400/25 bg-amber-400/5 px-3 py-2 text-[12px] text-amber-100 flex items-center gap-2"
                >
                  <AlertTriangle size={12} />
                  <span>{a.anomalies?.join(", ")}</span>
                  <span className="text-white/50">·</span>
                  <span className="text-white/70">{new Date(a.started_at).toLocaleDateString("it-IT")}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-white/50 text-sm">Nessuna anomalia</div>
          )}
        </Panel>
      </div>
    </div>
  );
}
